// PR #974 (AI cockpit): the context the docked cockpit binds to a route.
//
// This module is intentionally pure: it takes the current pathname and the
// shell selection registry and returns the entity context the cockpit may use.
// It is the single place that decides "which record is the person looking at",
// so the panel, the chips and the conversation creation all derive their input
// from one auditable function.
//
// Two invariants matter and are enforced here, not in the components:
//
//   1. Only a route this shell explicitly supports produces an entity context.
//      Every other path (settings, admin, marketing, an unknown section) maps to
//      `none` with no id, so the cockpit can never claim a record it is not on.
//   2. A presentation label never becomes an identity. The id always comes from
//      the URL segment and is validated against the same pattern the client
//      action contract uses; `entityLabel` is display-only and is left empty
//      unless the selection registry actually matches the route. Conversation
//      creation therefore only ever receives a validated type + id, never a
//      human-readable string.

import type { SelectionItem, SelectionKind } from '../../../shared/selection/useSelectionStore.js';

/** Entity kinds the cockpit can bind a conversation to (`none` = list/shell). */
export type ChatCockpitEntityType = 'deal' | 'contact' | 'project' | 'none';

/** Sections the cockpit is willing to dock on. Anything else is unsupported. */
export type ChatCockpitSection = 'deals' | 'contacts' | 'projects' | 'none';

export interface ChatCockpitContext {
  readonly entityType: ChatCockpitEntityType;
  readonly entityId: string;
  /** Display-only; empty when the selection does not match the route. */
  readonly entityLabel: string;
  /** The pathname the context was derived from. */
  readonly route: string;
}

/** Minimal shape of the shell selection registry this mapper reads. */
export interface ChatCockpitSelection {
  readonly kind: SelectionKind | null;
  readonly items: readonly SelectionItem[];
}

/**
 * Mirrors `AI_CHAT_CLIENT_ENTITY_ID_PATTERN`: one route segment that cannot
 * contain a separator or a relative marker, so a stored id can never become a
 * different path. Kept in sync by hand rather than imported so this pure mapper
 * has no runtime dependency on the chat kit.
 */
const CHAT_COCKPIT_ENTITY_ID_PATTERN = /^[A-Za-z0-9][A-Za-z0-9_-]{0,199}$/;

const SECTION_ENTITY_TYPE: Record<'deals' | 'contacts' | 'projects', ChatCockpitEntityType> = {
  deals: 'deal',
  contacts: 'contact',
  projects: 'project',
};

function pathSegments(pathname: string): string[] {
  return pathname.split('/').filter((segment) => segment !== '');
}

/** The supported section of a pathname, or `none`. This is the allowlist. */
export function chatCockpitSection(pathname: string): ChatCockpitSection {
  const [section] = pathSegments(pathname);
  if (section === 'deals' || section === 'contacts' || section === 'projects') return section;
  return 'none';
}

/**
 * True for the routes the cockpit may dock on: an exact list route
 * (`/deals`, `/contacts`, `/projects`) or a single card route
 * (`/deals/:id`, …). Deeper or malformed paths stay unsupported.
 */
export function isChatCockpitRoute(pathname: string): boolean {
  const section = chatCockpitSection(pathname);
  if (section === 'none') return false;
  const segments = pathSegments(pathname);
  if (segments.length === 1) return true;
  if (segments.length === 2) return CHAT_COCKPIT_ENTITY_ID_PATTERN.test(segments[1] ?? '');
  return false;
}

function selectedItem(
  selection: ChatCockpitSelection,
  entityType: ChatCockpitEntityType,
): SelectionItem | undefined {
  // Label leaks are the whole risk here: only a single selected row of exactly
  // the kind the route owns may contribute a label.
  if (selection.kind !== entityType) return undefined;
  if (selection.items.length !== 1) return undefined;
  return selection.items[0];
}

export function resolveChatCockpitContext(
  pathname: string,
  selection: ChatCockpitSelection,
): ChatCockpitContext {
  const route = pathname;
  const section = chatCockpitSection(pathname);
  if (section === 'none') {
    return { entityType: 'none', entityId: '', entityLabel: '', route };
  }

  const entityType = SECTION_ENTITY_TYPE[section];
  const selected = selectedItem(selection, entityType);
  const segments = pathSegments(pathname);

  if (segments.length === 1) {
    // List route: there is no single record, so the cockpit chats about the
    // section itself. The section keeps its selection only as a display label.
    return { entityType: 'none', entityId: '', entityLabel: selected?.label ?? '', route };
  }

  if (segments.length === 2) {
    const candidate = segments[1] ?? '';
    if (CHAT_COCKPIT_ENTITY_ID_PATTERN.test(candidate)) {
      return {
        entityType,
        entityId: candidate,
        entityLabel: selected !== undefined && selected.id === candidate ? selected.label : '',
        route,
      };
    }
  }

  // A path under a known section that is not a valid card/list route: fail
  // closed to `none` instead of guessing an entity.
  return { entityType: 'none', entityId: '', entityLabel: '', route };
}
